import { useCallback, useRef, useState, useEffect } from 'react';
import type { ClientMessage, RequestVisionFrameMessage, ServerMessage } from '@stream-buddy/shared';
import { useSessionStore } from '../stores/sessionStore';
import type { GameStat } from '../stores/sessionStore';

// localStorage key for tracking if user was live before server restart
const WAS_LIVE_KEY = 'streambuddy_was_live';
const CONNECTION_INTENT_KEY = 'streambuddy_connection_intent';
const PING_INTERVAL_MS = 30000;
const PONG_TIMEOUT_MS = 10000;

function normalizeGameStats(stats: Record<string, unknown>): Record<string, GameStat> {
  const normalized: Record<string, GameStat> = {};

  for (const [id, value] of Object.entries(stats)) {
    if (!value || typeof value !== 'object') continue;
    const stat = value as Record<string, unknown>;
    if (typeof stat.value !== 'number' || typeof stat.updatedAt !== 'number') continue;

    normalized[id] = {
      value: stat.value,
      updatedAt: stat.updatedAt,
      label: typeof stat.label === 'string' ? stat.label : undefined,
      lastUpdateMetadata: typeof stat.lastUpdateMetadata === 'string' ? stat.lastUpdateMetadata : undefined,
    };
  }

  return normalized;
}

/**
 * Parse AI response into thought (reasoning) and speech (actual response)
 * The AI separates thoughts from speech with triple newlines (\n\n\n)
 */
function parseThoughtAndSpeech(text: string): { thought?: string; speech: string } {
  const stripStageDirections = (value: string): string => value
    .replace(/\[[^\]\r\n]+\]\s*/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  const trimmed = text.trim();

  // Split on triple newline - thought before, speech after
  const parts = trimmed.split(/\n\n\n+/);

  if (parts.length >= 2) {
    // First part(s) are thought, last part is speech
    const thought = parts.slice(0, -1).join('\n\n\n').trim();
    const speech = parts[parts.length - 1].trim();
      return { thought: thought || undefined, speech: stripStageDirections(speech).replace(/\s{2,}/g, ' ').trim() || speech };
  }

  // Fallback for newer transcript format where reasoning lines are prefixed
  // with "Thinking:" and speech follows on later lines.
  const lines = trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.some((line) => /^thinking:/i.test(line))) {
    const thoughtLines: string[] = [];
    const speechLines: string[] = [];
    let collectingThought = true;

    for (const line of lines) {
      if (collectingThought && /^thinking:/i.test(line)) {
        thoughtLines.push(line.replace(/^thinking:\s*/i, ''));
        continue;
      }

      collectingThought = false;
      speechLines.push(line);
    }

    if (thoughtLines.length > 0) {
      const thought = thoughtLines.join('\n').trim();
      const speech = speechLines.join(' ').trim();
        const cleanedSpeech = stripStageDirections(speech).replace(/\s{2,}/g, ' ').trim();
        return { thought: thought || undefined, speech: cleanedSpeech || trimmed };
    }
  }

  // No triple newline separator - entire text is speech
  return { speech: stripStageDirections(trimmed).replace(/\s{2,}/g, ' ').trim() || trimmed };
}

interface UseWebSocketOptions {
  onConnected?: (context: { isReconnect: boolean }) => void;
  onDisconnected?: (code?: number) => void;
  onClose?: (code: number) => void;
  onError?: (error: Event) => void;
  onMessage?: (message: ServerMessage) => void;
  onAudioOutput?: (audioBuffer: ArrayBuffer) => void;
  persistIntent?: boolean;
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const wsRef = useRef<WebSocket | null>(null);
  const [lastAudioOutput, setLastAudioOutput] = useState<ArrayBuffer | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectingRef = useRef(false); // Track if we're in reconnecting mode (exposed for DesktopDashboard)
  const maxReconnectAttempts = 5;
  const subscriptionRejectedRef = useRef(false);
  const intentionallyConnectedRef = useRef(false); // Track if we WANT to be connected
  const currentUrlRef = useRef<string | null>(null);
  const sessionStartedThisConnectionRef = useRef(false); // Track if we started a session THIS connection
  const pingIntervalRef = useRef<number | null>(null);
  const pongTimeoutRef = useRef<number | null>(null);
  const awaitingPongRef = useRef(false);
  const lastSpeakingEpochRef = useRef<number>(0);
  const lastThinkingEpochRef = useRef<number>(0);
  const lastTurnEventEpochRef = useRef<number>(0);
  const pendingVisionRequestRef = useRef<RequestVisionFrameMessage | null>(null);
  const audioGenerationRef = useRef(0);

  // Use refs for callbacks to avoid stale closures
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const {
    setConnected,
    setMuted,
    setAiSpeaking,
    setChatConnected,
    setGeminiConnected,
    setOverlayConnected,
    setStopping,
    setReconnecting,
    setReconnectFailed,
    setAiThinking,
    setSessionHealth,
    updateStats,
    addTranscriptEntry
  } = useSessionStore();

  // Use ref for handleMessage to avoid circular dependency with connect
  const handleMessageRef = useRef<(message: ServerMessage) => void>(() => { });

  // Define handleMessage and update the ref
  const handleMessage = useCallback((message: ServerMessage) => {
    const messageEpoch = (message as { epoch?: number }).epoch;

    if (message.type === 'speaking_state' && typeof messageEpoch === 'number') {
      if (messageEpoch <= lastSpeakingEpochRef.current) {
        return;
      }
      lastSpeakingEpochRef.current = messageEpoch;
    }

    if (message.type === 'thinking_state' && typeof messageEpoch === 'number') {
      if (messageEpoch <= lastThinkingEpochRef.current) {
        return;
      }
      lastThinkingEpochRef.current = messageEpoch;
    }

    if (message.type === 'turn_event' && typeof messageEpoch === 'number') {
      if (messageEpoch <= lastTurnEventEpochRef.current) {
        return;
      }
      lastTurnEventEpochRef.current = messageEpoch;
    }

    if (message.type === 'request_vision_frame') {
      pendingVisionRequestRef.current = message;
    }

    // Call external handler after stale-message filtering
    optionsRef.current.onMessage?.(message);

    const maybeSessionHealth = message as {
      type?: string;
      degraded?: boolean;
      severity?: 'info' | 'warning' | 'critical';
      reasons?: string[];
      sampledAt?: number;
    };

    if (maybeSessionHealth.type === 'session_health') {
      setSessionHealth({
        degraded: !!maybeSessionHealth.degraded,
        severity: maybeSessionHealth.severity ?? 'info',
        reasons: maybeSessionHealth.reasons ?? [],
        sampledAt: maybeSessionHealth.sampledAt ?? Date.now(),
      });
      return;
    }

    switch (message.type) {
      case 'audio_output':
        // setLastAudioOutput(message.data); // Legacy base64 audio - binary handled in onmessage
        break;

      case 'speaking_state':
        setAiSpeaking(message.isSpeaking);
        break;

      case 'thinking_state':
        setAiThinking(message.isThinking);
        break;

      case 'status':
        setChatConnected(message.chatConnected, message.chatAuthenticated);
        setGeminiConnected(message.geminiConnected);
        setOverlayConnected(message.overlayConnected);
        setMuted(message.muted, message.muteExpiresAt);

        // Track stopping state
        if (message.isStopping !== undefined) {
          setStopping(message.isStopping);
        }

        // Track session connected state (for detecting when session stops)
        // Note: server sends 'connected' not 'isConnected'
        setConnected(message.connected);

        // Note: Auto-restart on server restart is handled by DesktopDashboard's onConnected callback
        // which has better context about whether this is a fresh connect vs reconnect
        break;

      case 'session_stats':
        updateStats({
          messagesAnalyzed: message.messagesAnalyzed,
          alertsTriggered: message.alertsTriggered,
          donationsRead: message.donationsRead,
          gameStats: normalizeGameStats(message.gameStats),
        });
        break;

      case 'error':
        console.error('[WebSocket] Server error:', message.code, message.message);
        // Stop reconnecting if subscription rejected
        if (message.message?.includes('subscription')) {
          subscriptionRejectedRef.current = true;
        }
        break;

      case 'pong':
        awaitingPongRef.current = false;
        if (pongTimeoutRef.current) {
          window.clearTimeout(pongTimeoutRef.current);
          pongTimeoutRef.current = null;
        }
        break;

      case 'session_restarted':
        // Session was restarted by server
        break;

      case 'clear_audio':
        // Audio cleared (interrupt) - dashboard doesn't play audio so nothing to do
        break;

      case 'transcript':
        // Add AI response transcript to activity log
        // Parse thought vs spoken text: anything before the last sentence tends to be reasoning
        if (message.text) {
          const { thought, speech } = parseThoughtAndSpeech(message.text);
          addTranscriptEntry(speech, thought, message.source ?? 'direct');
        }
        break;

      case 'ptt_start_ack':
      case 'ptt_end_ack':
      case 'turn_event':
        // handled by DesktopDashboard specific onMessage callback
        break;

      default:
        // Unknown message type - ignore
        break;
    }
  }, [setMuted, setAiSpeaking, setAiThinking, setSessionHealth, setChatConnected, setGeminiConnected, setOverlayConnected, setStopping, setConnected, addTranscriptEntry, updateStats]);

  // Keep ref in sync with latest handleMessage
  useEffect(() => {
    handleMessageRef.current = handleMessage;
  }, [handleMessage]);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const clearPongTimeout = useCallback(() => {
    if (pongTimeoutRef.current) {
      window.clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (pingIntervalRef.current) {
      window.clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    clearPongTimeout();
    awaitingPongRef.current = false;
  }, [clearPongTimeout]);

  const startHeartbeat = useCallback((ws: WebSocket) => {
    stopHeartbeat();
    pingIntervalRef.current = window.setInterval(() => {
      if (ws !== wsRef.current || ws.readyState !== WebSocket.OPEN) {
        return;
      }

      if (awaitingPongRef.current) {
        console.warn('[WebSocket] Pong timeout, forcing reconnect');
        ws.close(4000, 'Pong timeout');
        return;
      }

      awaitingPongRef.current = true;
      ws.send(JSON.stringify({ type: 'ping' }));
      clearPongTimeout();
      pongTimeoutRef.current = window.setTimeout(() => {
        if (awaitingPongRef.current && ws === wsRef.current && ws.readyState === WebSocket.OPEN) {
          console.warn('[WebSocket] Connection stale, no pong received');
          ws.close(4000, 'Pong timeout');
        }
      }, PONG_TIMEOUT_MS);
    }, PING_INTERVAL_MS);
  }, [clearPongTimeout, stopHeartbeat]);

  const resetRealtimeUiState = useCallback(() => {
    setAiSpeaking(false);
    setAiThinking(false);
    setSessionHealth(null);
    lastSpeakingEpochRef.current = 0;
    lastThinkingEpochRef.current = 0;
    lastTurnEventEpochRef.current = 0;
    pendingVisionRequestRef.current = null;
  }, [setAiSpeaking, setAiThinking, setSessionHealth]);

  const connect = useCallback((url: string) => {
    const existingSocket = wsRef.current;
    if (
      existingSocket
      && currentUrlRef.current === url
      && (existingSocket.readyState === WebSocket.CONNECTING || existingSocket.readyState === WebSocket.OPEN)
    ) {
      return;
    }

    // Clear any pending reconnection
    clearReconnectTimeout();

    // Close existing connection if any
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close();
    }

    intentionallyConnectedRef.current = true;
    if (optionsRef.current.persistIntent !== false) {
      localStorage.setItem(CONNECTION_INTENT_KEY, 'true'); // Persist across remounts
    }
    currentUrlRef.current = url;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (ws !== wsRef.current) {
        return;
      }

      // Clear stale UI state on fresh socket lifecycle
      resetRealtimeUiState();

      // Check if this is a reconnect (attempts > 0) before resetting counter
      const isReconnect = reconnectAttempts.current > 0;

      reconnectAttempts.current = 0;
      reconnectingRef.current = false; // Reset reconnecting flag
      // Clear any pending reconnect timeout (defensive)
      clearReconnectTimeout();
      setReconnecting(false);
      setReconnectFailed(false);

      // On reconnect after server restart, preserve session state for auto-recovery
      // On fresh connect, sessionStartedThisConnectionRef stays false until user clicks Go Live
      if (isReconnect && localStorage.getItem(WAS_LIVE_KEY) === 'true') {
        sessionStartedThisConnectionRef.current = true;
      }

      // Start ping interval
      startHeartbeat(ws);

      optionsRef.current.onConnected?.({ isReconnect });
    };

    ws.onclose = (event) => {
      if (ws !== wsRef.current) {
        return;
      }

      optionsRef.current.onClose?.(event.code);

      stopHeartbeat();

      wsRef.current = null;

      // Clear stale AI status when socket drops/reloads
      resetRealtimeUiState();
      setConnected(false);

      // Reset connection states to show disconnected in UI
      setChatConnected(false, false);
      setGeminiConnected(false);
      setOverlayConnected(false);

      optionsRef.current.onDisconnected?.(event.code);

      // Check if we WANT to be connected (use localStorage for persistence across remounts)
      const wantsConnection = intentionallyConnectedRef.current || (optionsRef.current.persistIntent !== false && localStorage.getItem(CONNECTION_INTENT_KEY) === 'true');

      // Don't reconnect if we intentionally disconnected
      if (!wantsConnection) {
        setReconnecting(false);
        return;
      }

      // Don't reconnect if subscription was rejected
      if (subscriptionRejectedRef.current) {
        setReconnecting(false);
        return;
      }

      // Don't reconnect on auth failures (4001 = no token, 4002 = invalid token)
      if (event.code === 4001 || event.code === 4002) {
        setReconnecting(false);
        setReconnectFailed(true);
        // Reset session state to prevent auto-restart loops
        sessionStartedThisConnectionRef.current = false;
        localStorage.removeItem(WAS_LIVE_KEY);
        return;
      }

      // Rate limit (4004) should cool down and retry instead of leaving demo stuck
      if (event.code === 4004 && currentUrlRef.current) {
        if (reconnectAttempts.current >= maxReconnectAttempts) {
          setReconnecting(false);
          setReconnectFailed(true);
          setConnected(false);
          localStorage.removeItem(WAS_LIVE_KEY);
          localStorage.removeItem(CONNECTION_INTENT_KEY);
          reconnectAttempts.current = 0;
          return;
        }

        setReconnecting(true);
        reconnectingRef.current = true;
        reconnectTimeoutRef.current = window.setTimeout(() => {
          reconnectAttempts.current = Math.min(reconnectAttempts.current + 1, maxReconnectAttempts);
          if (currentUrlRef.current) {
            connect(currentUrlRef.current);
          }
        }, 2500);
        return;
      }

      // Attempt reconnection
      if (reconnectAttempts.current < maxReconnectAttempts && currentUrlRef.current) {
        setReconnecting(true);
        reconnectingRef.current = true; // Mark as reconnecting for DesktopDashboard
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        reconnectTimeoutRef.current = window.setTimeout(() => {
          reconnectAttempts.current++;
          if (currentUrlRef.current) {
            connect(currentUrlRef.current);
          }
        }, delay);
      } else {
        // Max reconnect attempts reached - reset to "Go Live" state
        setReconnecting(false);
        setReconnectFailed(true);
        setConnected(false);

        // Clear the connection intent flags
        localStorage.removeItem(WAS_LIVE_KEY);
        localStorage.removeItem(CONNECTION_INTENT_KEY);

        // Reset attempt counter for next time
        reconnectAttempts.current = 0;
      }
    };

    ws.onerror = (error) => {
      if (ws !== wsRef.current) {
        return;
      }

      console.error('[WebSocket] Error:', error);
      optionsRef.current.onError?.(error);
    };

    ws.onmessage = (event) => {
      if (ws !== wsRef.current) {
        return;
      }

      // Check if binary message (audio)
      if (event.data instanceof Blob) {
        const currentWs = wsRef.current;
        const audioGeneration = audioGenerationRef.current;
        event.data.arrayBuffer()
          .then((buffer) => {
            // Only update if this is still the current websocket
            if (wsRef.current === currentWs && currentWs?.readyState === WebSocket.OPEN && audioGenerationRef.current === audioGeneration) {
              optionsRef.current.onAudioOutput?.(buffer);
              setLastAudioOutput(buffer);
            }
          })
          .catch((err) => {
            console.error('[WebSocket] Failed to convert audio blob:', err);
          });
        return;
      }

      try {
        const message = JSON.parse(event.data as string) as ServerMessage;
        if (message.type === 'clear_audio' || message.type === 'session_restarted') {
          audioGenerationRef.current += 1;
        }
        handleMessageRef.current(message);
      } catch (e) {
        console.error('[WebSocket] Failed to parse message:', e);
      }
    };
  }, [clearReconnectTimeout, resetRealtimeUiState, setChatConnected, setConnected, setGeminiConnected, setOverlayConnected, setReconnectFailed, setReconnecting, startHeartbeat, stopHeartbeat]);

  const disconnect = useCallback(() => {
    // Mark that we don't want to be connected (both ref and localStorage)
    intentionallyConnectedRef.current = false;
    if (optionsRef.current.persistIntent !== false) {
      localStorage.removeItem(CONNECTION_INTENT_KEY);
    }
    currentUrlRef.current = null;

    clearReconnectTimeout();
    stopHeartbeat();
    reconnectAttempts.current = maxReconnectAttempts; // Prevent reconnection
    setConnected(false);
    resetRealtimeUiState();
    wsRef.current?.close();
  }, [clearReconnectTimeout, resetRealtimeUiState, setConnected, stopHeartbeat]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  const sendMessage = useCallback((message: ClientMessage): boolean => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  const sendAudio = useCallback((audioData: ArrayBuffer): boolean => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(audioData);
      return true;
    }
    return false;
  }, []);

  const sendPttStart = useCallback((traceId?: string) => {
    sendMessage({ type: 'ptt_start', traceId });
  }, [sendMessage]);

  const sendEndAudioTurn = useCallback((traceId?: string) => {
    sendMessage({ type: 'end_audio_turn', traceId });
  }, [sendMessage]);

  const sendVision = useCallback((base64Data: string) => {
    const request = pendingVisionRequestRef.current;
    if (!request || request.expiresAt < Date.now()) {
      pendingVisionRequestRef.current = null;
      return false;
    }
    pendingVisionRequestRef.current = null;
    sendMessage({
      type: 'vision_frame',
      requestId: request.requestId,
      turnId: request.turnId,
      turnSeq: request.turnSeq,
      mimeType: 'image/jpeg',
      data: base64Data,
    });
    return true;
  }, [sendMessage]);

  const restartSession = useCallback(() => {
    sendMessage({ type: 'restart_session' });
  }, [sendMessage]);

  const startSession = useCallback(() => {
    if (optionsRef.current.persistIntent !== false) {
      localStorage.setItem(WAS_LIVE_KEY, 'true');
    }
    sessionStartedThisConnectionRef.current = true; // Mark that we started a session this lifecycle
    sendMessage({ type: 'start_session' });
  }, [sendMessage]);

  const stopSession = useCallback(() => {
    if (optionsRef.current.persistIntent !== false) {
      localStorage.removeItem(WAS_LIVE_KEY);
    }
    sessionStartedThisConnectionRef.current = false; // Reset so next Go Live doesn't trigger auto-restart
    setAiSpeaking(false);
    setAiThinking(false);
    sendMessage({ type: 'stop_session' });
  }, [sendMessage, setAiSpeaking, setAiThinking]);

  // Test function for session resumption (call from browser console)
  const testSessionResumption = useCallback((delaySeconds: number = 5) => {
    sendMessage({ type: 'test_session_resumption', delay: delaySeconds });
  }, [sendMessage]);

  // Expose test function on window for console access
  useEffect(() => {
    (window as unknown as { testSessionResumption: (delay?: number) => void }).testSessionResumption = testSessionResumption;
    return () => {
      delete (window as unknown as { testSessionResumption?: (delay?: number) => void }).testSessionResumption;
    };
  }, [testSessionResumption]);

  return {
    connect,
    disconnect,
    sendMessage,
    sendAudio,
    sendPttStart,
    sendEndAudioTurn,
    sendVision,
    restartSession,
    startSession,
    stopSession,
    lastAudioOutput,
    reconnectingRef,
  };
}
